
import { supabase } from "@/integrations/supabase/client";
import { PlacesSearchResponse } from "@/types/place";

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
const MAX_RETRIES = 2;
const RETRYABLE_STATUSES = new Set(['UNKNOWN_ERROR', 'OVER_QUERY_LIMIT', 'INTERNAL_ERROR']);

async function invokePlacesWithRetry(body: Record<string, any>): Promise<PlacesSearchResponse> {
  let attempt = 0;
  while (true) {
    try {
      const response = await supabase.functions.invoke('places', { body });

      if (response.error) {
        console.warn('Places invoke error (network/transport):', response.error, 'attempt:', attempt + 1);
        if (attempt < MAX_RETRIES) {
          await sleep(800 * (attempt + 1));
          attempt++;
          continue;
        }
        return {
          results: [],
          status: 'ERROR',
          error_message: 'Network error occurred while searching',
          user_friendly_message: 'Unable to connect to search service. Please check your internet connection and try again.'
        };
      }

      const data = response.data;

      // Fallback normalization if backend didn't normalize
      if (data?.status === 'ZERO_RESULTS') {
        return {
          ...data,
          status: 'OK',
          results: [],
          info_message: data.info_message || 'No businesses found matching your search criteria',
        };
      }

      // Retry on transient statuses
      if (data && RETRYABLE_STATUSES.has(data.status)) {
        console.warn('Retryable Places status:', data.status, 'attempt:', attempt + 1);
        if (attempt < MAX_RETRIES) {
          await sleep(1000 * (attempt + 1));
          attempt++;
          continue;
        }
      }

      return data;
    } catch (err) {
      console.error('Exception invoking places:', err, 'attempt:', attempt + 1);
      if (attempt < MAX_RETRIES) {
        await sleep(800 * (attempt + 1));
        attempt++;
        continue;
      }
      return {
        results: [],
        status: 'ERROR',
        error_message: err instanceof Error ? err.message : 'Unknown error',
        user_friendly_message: 'An unexpected error occurred. Please try again.'
      };
    }
  }
}

export const searchPlaces = async (
  query: string, 
  location: string,
  radius: number,
  useRadius: boolean = true
): Promise<PlacesSearchResponse> => {
  try {
    console.log('Searching places:', { query, location, radius, useRadius });
    
    const data = await invokePlacesWithRetry({ query, location, radius, useRadius });

    console.log('Search response:', {
      status: data?.status,
      resultsCount: data?.results?.length,
      hasError: !!data?.error_message
    });

    return data;
  } catch (error) {
    console.error("Exception searching places:", error);
    return { 
      results: [], 
      status: 'ERROR',
      error_message: error instanceof Error ? error.message : 'Unknown error',
      user_friendly_message: 'An unexpected error occurred. Please try again.'
    };
  }
};

export const fetchNextPage = async (
  pageToken: string,
  meta?: {
    query?: string;
    location?: string;
    radius?: number;
    geocoded?: boolean;
    endpoint?: string;
    useRadius?: boolean;
  }
): Promise<PlacesSearchResponse> => {
  try {
    console.log('Fetching next page with token:', pageToken.substring(0, 20) + '...');
    console.log('Meta data being passed:', meta);
    
    // Respect Google next_page_token delay
    await sleep(2000);
    
    const data = await invokePlacesWithRetry({
      pageToken,
      query: meta?.query,
      location: meta?.location,
      radius: meta?.radius,
      useRadius: meta?.useRadius,
      geocoded: meta?.geocoded,
      endpoint: meta?.endpoint,
      meta
    });

    console.log('Next page data received:', {
      status: data?.status,
      resultsCount: data?.results?.length,
      nextPageToken: data?.next_page_token ? 'exists' : 'none',
      meta: data?.meta
    });

    return data;
  } catch (error) {
    console.error("Error fetching next page:", error);
    return { 
      results: [], 
      status: 'ERROR',
      error_message: error instanceof Error ? error.message : 'Unknown error',
      user_friendly_message: 'An unexpected error occurred loading more results.'
    };
  }
};

export const searchPlacesFromSaved = async (
  savedSearch: {
    id: string;
    name: string;
    query?: string;
    location?: string;
    radius: number;
  },
  useRadius: boolean = true
): Promise<PlacesSearchResponse> => {
  return searchPlaces(
    savedSearch.query || '',
    savedSearch.location || '',
    savedSearch.radius,
    useRadius
  );
};
